import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CommonConstants } from '../../../../constants/common-constant';
import confetti from 'canvas-confetti';
import { CommonService } from '../../../../services/common.service';
import { SnackbarService } from '../../../../shared/snackbar/snackbar.service';
import { CommentDialogComponent } from '../../../../shared/components/comment-dialog/comment-dialog.component';
import { CancelModalComponent } from '../cancel-modal/cancel-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { CalendarDataRefreshService } from '../../../../services/calendar-data-refresh.service';
import { CurrencyFormatPipe } from '../../../../shared/pipes/currency-format.pipe';
import { GuardDetailsModalComponent } from '../../../../shared/components/guard-details-modal/guard-details-modal.component';
import { Store } from '@ngrx/store';
import * as fromApp from "../../../../store/app.reducer"
import { firstValueFrom } from 'rxjs';
import { LoadingService } from 'src/app/service/loading-service.service';
import { CustomerModel } from 'src/app/models/customer.model';

const statusMap = {
  1: 'Publish',
  2: 'Ready',
  3: 'Ongoing',
  4: 'Finished',
  5: 'Canceled',
  6: 'Requesting Approval',
};

@Component({
  selector: "app-finished",
  templateUrl: "./finished.component.html",
  styleUrls: ["./finished.component.scss"],
  standalone: false
})
export class FinishedComponent implements OnInit, OnDestroy {
  //Variable declation section start
  finishAssignmentData: any;
  commonConstants = CommonConstants;
  private hasDataChanged: boolean = false;
  customerId: any;
  subscription: any;
  public customer: CustomerModel;
  isRequestApproal: boolean = false;

  // Overtime confirmation state
  showOvertimeConfirm: boolean = false;
  overtimeConfirmType: 'approve' | 'reject' | null = null;
  overtimeGuardData: any = null;

  // Rejection and correction popup state
  showRejectionPopup: boolean = false;
  showCorrectionPopup: boolean = false;
  correctionCheckinTime: string = '';
  correctionCheckoutTime: string = '';

  // Popup position for popover style
  popupPosition: { top: number; left: number; openDirection: 'left' | 'right' } = { top: 0, left: 0, openDirection: 'left' };
  //Variable declation section end

  //Lifecycle start
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<FinishedComponent>,
    private dialog: MatDialog,
    private commonService: CommonService,
    private snackbarService: SnackbarService,
    private translate: TranslateService,
    private calendarDataRefreshService: CalendarDataRefreshService,
    public currencyFormatPipe: CurrencyFormatPipe,
    private store: Store<fromApp.AppState>,
    private loadingService: LoadingService,
  ) { }

  async ngOnInit(): Promise<void> {
    try {
      const authState = await firstValueFrom(this.store.select(state => state.auth));
      this.customerId = authState?.customer?.id;
      this.finishAssignmentData = this.data;
      if (!this.customerId) {
        console.error('Customer ID not found.');
        return;
      }
      await this.loadShiftDetails();

    } catch (error) {
      console.error('Error initializing component:', error);
    }
    this.subscription = this.store.subscribe(appState => {
      this.customer = appState.auth.customer;
    });

    this.isRequestApproal = this.data?.isRequestApproval || this.data?.isRequestApproal || false;
  }

  ngOnDestroy() {
    if (this.hasDataChanged) {
      this.calendarDataRefreshService.triggerRefresh('shift_updated', this.finishAssignmentData?.id);
    }
  }

  private myConfetti: any;

  ngAfterViewInit() {
    const canvas = document.getElementById('confetti-canvas') as HTMLCanvasElement;
    this.myConfetti = confetti.create(canvas, {
      resize: true,
      useWorker: true
    });
  }
  //Lifecycle end 
  closeModal() {
    this.dialogRef.close();
  }

  get totalGuards(): number {
    return this.data.shifts.reduce((sum, s) => sum + s.guards.length, 0);
  }

  get totalHours(): number {
    return this.data.shifts.reduce((sum, s) => {
      return sum + s.guards.reduce((gs, g) => gs + g.hours, 0);
    }, 0);
  }

  get onTimePercentage(): number {
    const total = this.totalGuards;
    const onTime = this.data.shifts.reduce((sum, s) => {
      return sum + s.guards.filter(g => g.verified).length;
    }, 0);
    return total > 0 ? Math.round((onTime / total) * 100) : 0;
  }

  get positiveFeedbackPercentage(): number {
    const total = this.totalGuards;
    const positive = this.data.shifts.reduce((sum, s) => {
      return sum + s.guards.filter(g => g.feedback === 'positive').length;
    }, 0);
    return total > 0 ? Math.round((positive / total) * 100) : 0;
  }

  calculateDuration(checkin: string | undefined, checkout: string | undefined): string {
    if (!checkin || !checkout) return '-';

    const start = new Date(checkin);
    const end = new Date(checkout);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) return '-';

    const diffMs = end.getTime() - start.getTime();
    if (diffMs < 0) return '-';

    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    return `${diffHours}h ${diffMinutes}m`;
  }

  // Calculate total hours for all guards
  // If check-in and check-out times exist, use them; otherwise use shift duration
  getTotalHoursForShift(): number {
    if (!this.finishAssignmentData?.hired || this.finishAssignmentData.hired.length === 0) {
      return this.finishAssignmentData?.duration || 0;
    }

    let totalHours = 0;
    const shiftDate = this.finishAssignmentData?.startAt
      ? new Date(this.finishAssignmentData.startAt).toISOString().split('T')[0]
      : null;

    for (const guard of this.finishAssignmentData.hired) {
      const checkinTime = guard?.shiftDetails?.checkinTime;
      const checkoutTime = guard?.shiftDetails?.checkoutTime;

      // If both check-in and check-out times exist, calculate from those
      if (checkinTime && checkoutTime) {
        try {
          const start = new Date(checkinTime);
          const end = new Date(checkoutTime);

          if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
            const diffMs = end.getTime() - start.getTime();
            if (diffMs >= 0) {
              const hours = diffMs / (1000 * 60 * 60);
              totalHours += hours;
              continue;
            }
          }
        } catch (e) {
          // If parsing fails, fall through to use pass duration
        }
      }

      // Fallback: use actual shift start and end time (pass duration)
      if (this.finishAssignmentData?.startTime && this.finishAssignmentData?.endTime && shiftDate) {
        try {
          const startTime = this.finishAssignmentData.startTime;
          const endTime = this.finishAssignmentData.endTime;

          const start = new Date(`${shiftDate}T${startTime}:00`);
          let end = new Date(`${shiftDate}T${endTime}:00`);

          // Handle overnight shifts
          if (end <= start) {
            end.setDate(end.getDate() + 1);
          }

          if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
            const diffMs = end.getTime() - start.getTime();
            if (diffMs >= 0) {
              const hours = diffMs / (1000 * 60 * 60);
              totalHours += hours;
              continue;
            }
          }
        } catch (e) {
          // If parsing fails, use duration as last resort
        }
      }

      // Last resort: use the pass duration if available
      if (this.finishAssignmentData?.duration) {
        totalHours += this.finishAssignmentData.duration;
      }
    }
    return totalHours;
  }

  // Calculate late check-in time difference (e.g., "+25m")
  calculateLateMinutes(shiftDetails: any, scheduledStartTime: string): string {
    if (!shiftDetails?.checkinTime || !scheduledStartTime) return '';

    try {
      const checkinDate = new Date(shiftDetails.checkinTime.replace(' ', 'T'));
      if (isNaN(checkinDate.getTime())) return '';

      const [hours, minutes] = scheduledStartTime.split(':').map(Number);
      const scheduledStart = new Date(checkinDate);
      scheduledStart.setHours(hours, minutes, 0, 0);

      const diffMs = checkinDate.getTime() - scheduledStart.getTime();
      if (diffMs <= 0) return ''; // Not late

      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return `+${diffMinutes}m`;
    } catch (e) {
      return '';
    }
  }

  // Calculate overtime difference (e.g., "+45m")
  calculateOvertimeMinutes(shiftDetails: any, scheduledEndTime: string): string {
    if (!shiftDetails?.checkoutTime || !scheduledEndTime) return '';

    try {
      const checkoutDate = new Date(shiftDetails.checkoutTime.replace(' ', 'T'));
      if (isNaN(checkoutDate.getTime())) return '';

      const [hours, minutes] = scheduledEndTime.split(':').map(Number);
      const scheduledEnd = new Date(checkoutDate);
      scheduledEnd.setHours(hours, minutes, 0, 0);

      const diffMs = checkoutDate.getTime() - scheduledEnd.getTime();
      if (diffMs <= 0) return ''; // No overtime

      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return `+${diffMinutes}m`;
    } catch (e) {
      return '';
    }
  }

  displayStatus = (status: number | null | undefined) => {
    if (typeof status === 'number') {
      return this.translate.instant(`shift-status.${status}`) || this.translate.instant('shift-status.unknown');
    }
    return this.translate.instant('shift-status.unknown');
  };


  isGuardLate(shiftDetails: any, startTimeStr: string): boolean {
    // Validate checkinTime exists and is a non-empty string
    if (!shiftDetails?.checkinTime || typeof shiftDetails.checkinTime !== 'string') {
      return false;
    }

    // Validate startTimeStr format (HH:mm)
    if (!startTimeStr || !/^\d{1,2}:\d{2}$/.test(startTimeStr)) {
      return false;
    }

    try {
      // Replace space with 'T' to make it ISO compliant
      const checkinISO = shiftDetails.checkinTime.replace(' ', 'T');
      const checkin = new Date(checkinISO);

      if (isNaN(checkin.getTime())) {
        // Invalid date
        return false;
      }

      // Parse startTimeStr (like "10:46") into hours and minutes
      const [hours, minutes] = startTimeStr.split(':').map(Number);

      // Create a new Date for start time, same day as checkin
      const start = new Date(checkin);
      start.setHours(hours, minutes, 0, 0);

      // const isGuardLate = shiftDetails.guardIsLate === this.commonConstants.IS_GUARD_LATE_TRUE;
      return checkin > start;

    } catch (e) {
      // In case of any unexpected error, return false (not late)
      return false;
    }
  }

  openCommentModal(guardDetails) {
    const dialogRef = this.dialog.open(CommentDialogComponent, {
      width: '400px',
      data: {
        ...guardDetails,
        shiftId: this.finishAssignmentData.id
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
      }
    });
  }

  openCancelModal(guardDetails) {
    const dialogRef = this.dialog.open(CancelModalComponent, {
      width: '300px',
      data: {
        ...guardDetails,
        shiftId: this.finishAssignmentData.id,
        isBlockUSer: true,
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) {
        // Make a copy of the hired array
        this.finishAssignmentData.hired = this.finishAssignmentData.hired.map((g: any) => {
          if (g.id === guardDetails.id) {
            // Return a new object with updated flag
            return { ...g, isCustomerBlockedToGuard: true };
          }
          return g;
        });
      }
    });
  }

  handleStarGuard(guardId: any, event: MouseEvent) {
    const payload = {
      shiftId: this.finishAssignmentData.id,
      guardId: guardId,
      rate: 5
    };

    this.commonService.rateAndCommentToGuard(payload).subscribe({
      next: () => {
        // Make a copy of the hired array
        this.finishAssignmentData.hired = this.finishAssignmentData.hired.map((g: any) => {
          if (g.id === guardId) {
            // Return a new object with updated flag
            return { ...g, isRatedByCustomerForShift: true };
          }
          return g;
        });

        // Trigger calendar refresh immediately and reset flag to prevent duplicate calls
        this.calendarDataRefreshService.triggerRefresh('shift_updated', this.finishAssignmentData.id);
        this.hasDataChanged = false; // Reset flag since we already triggered refresh

        this.showConfettiEffect(event);
        this.snackbarService.showSuccess(this.translate.instant('guard-shift-details.finish.rating-sucessfull'));
      },
      error: () => {
        this.snackbarService.showError(this.translate.instant('guard-shift-details.finish.rating-error'));
      }
    });

  }

  // Separate confetti method for clarity
  showConfettiEffect(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const rect = target.getBoundingClientRect();
    const origin = {
      x: (rect.left + rect.width / 2) / window.innerWidth,
      y: (rect.top + rect.height / 2) / window.innerHeight,
    };
    try {
      const root = document.documentElement;
      const primaryVar = getComputedStyle(root).getPropertyValue('--primary').trim();
      const brand = primaryVar ? `hsl(${primaryVar})` : '#2563eb';

      this.myConfetti({
        particleCount: 28,
        spread: 60,
        startVelocity: 28,
        origin,
        ticks: 80,
        scalar: 0.9,
        colors: [brand, '#ffffff'],
      });
    } catch (error) {
      console.warn('Confetti effect failed:', error);
    }
  }

  getRemainingSkillsTooltip(skills: any[]): string {
    return skills
      .slice(1)
      .map(skill => `• ${skill?.name}`)
      .join('\n');
  }

  get calculatTotalCost() {
    const cost = this.finishAssignmentData?.costPerGuard;
    const guards = this.finishAssignmentData?.guardsNumber;
    const total = cost && guards ? cost * guards : 0;

    return total > 0 ? this.currencyFormatPipe.transform(total) : '–';
  }

  openGuardDetailsModal(guardDetails: any) {
    this.dialog.open(GuardDetailsModalComponent, {
      data: guardDetails
    });
  }

  private async loadShiftDetails(): Promise<void> {
    this.loadingService.showLoader();
    try {
      const response: any = await firstValueFrom(
        this.commonService.getShiftDetailsById(this.customerId, this.finishAssignmentData.id)
      );
      this.finishAssignmentData = response.data;
    } catch (error) {
      console.error('Something went wrong while fetching shift data:', error);
    }
    finally {
      this.loadingService.hideLoader();
    }
  }

  // Show confirmation popup for approve overtime
  showApproveOvertimeConfirm(guardData: any, event?: MouseEvent): void {
    this.overtimeGuardData = guardData;
    this.overtimeConfirmType = 'approve';

    // Calculate popup position based on click location and available space
    // This popup is taller (has scheduled/reported times, tags, explanation, and buttons)
    if (event) {
      this.calculatePopupPosition(event, 380, 420);
    }

    this.showOvertimeConfirm = true;
  }

  // Show confirmation popup for reject overtime
  showRejectOvertimeConfirm(guardData: any): void {
    this.overtimeGuardData = guardData;
    this.overtimeConfirmType = 'reject';
    this.showOvertimeConfirm = true;
  }

  // Cancel overtime confirmation
  cancelOvertimeConfirm(): void {
    this.showOvertimeConfirm = false;
    this.overtimeConfirmType = null;
    this.overtimeGuardData = null;
  }

  // Confirm overtime action (Yes button)
  async confirmOvertimeAction(): Promise<void> {
    if (this.overtimeConfirmType === 'approve') {
      await this.approveOvertime(this.overtimeGuardData);
      this.cancelOvertimeConfirm();
    } else if (this.overtimeConfirmType === 'reject') {
      // First call reject API, then show rejection popup
      await this.rejectOvertimeAndShowPopup(this.overtimeGuardData);
    }
  }

  // Reject overtime and show rejection popup
  private async rejectOvertimeAndShowPopup(guardData: any): Promise<void> {
    // Close overtime confirm popup first
    this.showOvertimeConfirm = false;

    // Get check-in and check-out times from guard's shift details and convert to UTC
    const checkinTimeUTC = this.convertToUTC(guardData?.shiftDetails?.checkinTime);
    const checkoutTimeUTC = this.convertToUTC(guardData?.shiftDetails?.checkoutTime);

    this.loadingService.showLoader();
    try {
      await firstValueFrom(
        this.commonService.rejectOvertime(
          this.finishAssignmentData.id,
          guardData.id,
          checkinTimeUTC,
          checkoutTimeUTC
        )
      );

      // Reload shift details from API to get the updated data
      await this.loadShiftDetails();

      // Update assignment status from "Requesting Approval" (6) to "Finished" (4)
      this.finishAssignmentData.status = 4;
      this.isRequestApproal = false;

      this.hasDataChanged = true;

      // Hide loader before showing popup
      this.loadingService.hideLoader();

      // Wait for DOM to update with the new "Rejected" badge, then position popup next to it
      // Use longer timeout to ensure Angular change detection completes
      setTimeout(() => {
        this.positionPopupNextToRejectedBadge(guardData.id);
        this.showRejectionPopup = true;
      }, 500);

    } catch (error) {
      console.error('Error rejecting overtime:', error);
      this.snackbarService.showError(this.translate.instant('guard-shift-details.finish.overtime-reject-error'));
      this.loadingService.hideLoader();
    }
  }

  // Find the rejected badge element and position popup next to it
  private positionPopupNextToRejectedBadge(guardId: any): void {
    // Convert guardId to string for comparison (in case it's number vs string mismatch)
    const guardIdStr = String(guardId);

    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const popupWidth = 320;
    const popupHeight = 200;
    const isMobile = windowWidth < 640; // Mobile breakpoint

    // Find all rejected badges for this guard and pick the visible one
    const allBadges = document.querySelectorAll(`span.rejected-badge[data-guard-id="${guardIdStr}"]`);
    let badge: HTMLElement | null = null;

    // Find the first visible badge (has non-zero dimensions)
    for (let i = 0; i < allBadges.length; i++) {
      const el = allBadges[i] as HTMLElement;
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0 && rect.top > 0) {
        badge = el;
        break;
      }
    }

    // Fallback: find any visible rejected badge if specific one not found
    if (!badge) {
      const rejectedBadges = document.querySelectorAll('span.rejected-badge');
      for (let i = 0; i < rejectedBadges.length; i++) {
        const el = rejectedBadges[i] as HTMLElement;
        const rect = el.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0 && rect.top > 0) {
          badge = el;
          break;
        }
      }
    }

    if (badge) {
      const rect = badge.getBoundingClientRect();

      // Calculate top - position popup at the same vertical level as the button
      let top = rect.top;

      // Ensure popup doesn't go off bottom of screen
      if (top + popupHeight > windowHeight - 20) {
        top = windowHeight - popupHeight - 20;
      }
      // Ensure popup doesn't go off top of screen
      if (top < 20) {
        top = 20;
      }

      let left: number;

      if (isMobile) {
        // On mobile: center the popup horizontally
        left = (windowWidth - popupWidth) / 2;
      } else {
        // On desktop: position to the LEFT of the button
        left = rect.left - popupWidth - 10;

        // If not enough space on left, keep it at minimum left margin
        if (left < 10) {
          left = 10;
        }
      }

      this.popupPosition = {
        top: top,
        left: left,
        openDirection: 'left'
      };
    } else {
      // Fallback: if no badge found, position popup in a reasonable location
      // Try to find the table/STATUS column area
      const statusColumn = document.querySelector('th:contains("STATUS"), td.whitespace-nowrap');

      if (isMobile) {
        // On mobile: center the popup
        this.popupPosition = {
          top: windowHeight / 2 - popupHeight / 2,
          left: (windowWidth - popupWidth) / 2,
          openDirection: 'left'
        };
      } else {
        // On desktop: position in the middle-right area of the screen
        this.popupPosition = {
          top: windowHeight / 2 - popupHeight / 2,
          left: windowWidth / 2 - popupWidth / 2,
          openDirection: 'left'
        };
      }
    }
  }

  // Open correction popup from rejection popup
  openCorrectionPopup(): void {
    // Pre-fill with scheduled times
    this.correctionCheckinTime = this.finishAssignmentData?.startTime || '08:00';
    this.correctionCheckoutTime = this.finishAssignmentData?.endTime || '16:00';
    this.showRejectionPopup = false;
    // Keep the same position as rejection popup - don't recalculate
    this.showCorrectionPopup = true;
  }

  // Cancel rejection popup
  cancelRejectionPopup(): void {
    this.showRejectionPopup = false;
    this.overtimeGuardData = null;
  }

  // Calculate popup position based on button location - ALWAYS position to the LEFT of the button
  private calculatePopupPosition(event: MouseEvent, popupWidth: number = 320, popupHeight: number = 280): void {
    // Get the clicked button element - find the closest clickable element (span or button)
    let target = event.currentTarget as HTMLElement;

    // If currentTarget is not available, use target and traverse up to find the clickable element
    if (!target) {
      target = event.target as HTMLElement;
      // Find the closest span or button parent (the actual clickable element)
      const clickableParent = target.closest('span, button') as HTMLElement;
      if (clickableParent) {
        target = clickableParent;
      }
    }

    const rect = target.getBoundingClientRect();

    const windowHeight = window.innerHeight;

    // Button position - use the RIGHT edge of the button as reference point
    const buttonRight = rect.right;
    const buttonTop = rect.top;
    const buttonCenter = buttonTop + rect.height / 2;

    // Calculate top - try to center popup vertically with the button
    let top = buttonCenter - popupHeight / 2;

    // Ensure popup doesn't go off bottom of screen
    if (top + popupHeight > windowHeight - 20) {
      top = windowHeight - popupHeight - 20;
    }
    // Ensure popup doesn't go off top of screen
    if (top < 20) {
      top = 20;
    }

    // Calculate left - ALWAYS position to the LEFT of the button
    // Position popup so its right edge is 10px to the left of button's left edge
    let left = rect.left - popupWidth - 10;

    // If not enough space on left, keep it at minimum left margin but still on left side
    if (left < 20) {
      left = 20;
    }

    this.popupPosition = {
      top: top,
      left: left,
      openDirection: 'left'
    };
  }

  // Open rejection popup for a guard that already has rejected status
  openRejectionPopupForGuard(guardData: any, event?: MouseEvent): void {
    this.overtimeGuardData = guardData;

    if (event) {
      // Stop propagation to prevent parent click handlers
      event.stopPropagation();
      this.calculatePopupPosition(event, 320, 200);
    }

    this.showRejectionPopup = true;
  }

  // Cancel correction popup
  cancelCorrectionPopup(): void {
    this.showCorrectionPopup = false;
    this.overtimeGuardData = null;
    this.correctionCheckinTime = '';
    this.correctionCheckoutTime = '';
  }

  // Save corrected time report
  async saveCorrectedTimeReport(): Promise<void> {
    if (
      !this.overtimeGuardData ||
      !this.correctionCheckinTime ||
      !this.correctionCheckoutTime ||
      !this.finishAssignmentData
    ) {
      return;
    }

    const scheduledStartTime = this.finishAssignmentData.startTime;
    const scheduledEndTime = this.finishAssignmentData.endTime;

    // Get shift date (YYYY-MM-DD)
    const shiftDate = new Date(this.finishAssignmentData.startAt);
    const dateStr = shiftDate.toISOString().split('T')[0];

    // Build corrected Date objects
    const checkinDate = new Date(`${dateStr}T${this.correctionCheckinTime}:00`);
    let checkoutDate = new Date(`${dateStr}T${this.correctionCheckoutTime}:00`);

    // Build scheduled Date objects
    const scheduledCheckin = scheduledStartTime
      ? new Date(`${dateStr}T${scheduledStartTime}:00`)
      : null;

    let scheduledCheckout = scheduledEndTime
      ? new Date(`${dateStr}T${scheduledEndTime}:00`)
      : null;

    // Detect overnight scheduled shift
    const isScheduledOvernightShift =
      scheduledCheckin && scheduledCheckout && scheduledCheckout <= scheduledCheckin;

    if (isScheduledOvernightShift && scheduledCheckout) {
      scheduledCheckout.setDate(scheduledCheckout.getDate() + 1);
    }

    // Detect overnight corrected shift
    const isCorrectedOvernightShift = checkoutDate <= checkinDate;
    if (isCorrectedOvernightShift) {
      checkoutDate.setDate(checkoutDate.getDate() + 1);
    }

    // ---------------- VALIDATIONS ----------------

    // Check-in cannot be before scheduled start
    if (scheduledCheckin && checkinDate < scheduledCheckin) {
      this.snackbarService.showError(
        this.translate.instant('guard-shift-details.finish.checkin-before-scheduled')
      );
      return;
    }

    // Check-in and checkout cannot be same
    if (checkinDate.getTime() === checkoutDate.getTime()) {
      this.snackbarService.showError(
        this.translate.instant('guard-shift-details.finish.checkin-greater-than-checkout')
      );
      return;
    }

    // Do not allow overnight correction if shift is not overnight
    if (!isScheduledOvernightShift && isCorrectedOvernightShift) {
      this.snackbarService.showError(
        this.translate.instant('guard-shift-details.finish.checkin-greater-than-checkout')
      );
      return;
    }

    this.loadingService.showLoader();

    try {
      // Convert to UTC format "Y-m-d H:i:s"
      const formattedCheckinTime = this.formatDateToUTC(checkinDate);
      const formattedCheckoutTime = this.formatDateToUTC(checkoutDate);

      await firstValueFrom(
        this.commonService.correctTimeReport(
          this.finishAssignmentData.id,
          this.overtimeGuardData.id,
          formattedCheckinTime,
          formattedCheckoutTime,
          301
        )
      );

      // Reload updated data
      await this.loadShiftDetails();

      // Update UI state
      this.finishAssignmentData.status = 4; // Finished
      this.isRequestApproal = false;
      this.hasDataChanged = true;

      this.snackbarService.showSuccess(
        this.translate.instant('guard-shift-details.finish.time-corrected')
      );

      this.cancelCorrectionPopup();
    } catch (error) {
      console.error('Error correcting time report:', error);
      this.snackbarService.showError(
        this.translate.instant('guard-shift-details.finish.time-correct-error')
      );
    } finally {
      this.loadingService.hideLoader();
    }
  }

  // Helper method to format date to UTC string "Y-m-d H:i:s"
  private formatDateToUTC(date: Date): string {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    const seconds = String(date.getUTCSeconds()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  // Helper method to convert datetime string to UTC format
  private convertToUTC(dateTimeStr: string | null): string | null {
    if (!dateTimeStr) return null;

    try {
      // Parse the datetime string and convert to Date object
      const date = new Date(dateTimeStr.replace(' ', 'T'));
      if (isNaN(date.getTime())) return null;

      // Format as UTC "Y-m-d H:i:s"
      return this.formatDateToUTC(date);
    } catch (e) {
      return null;
    }
  }

  private async approveOvertime(guardData: any): Promise<void> {
    this.loadingService.showLoader();
    try {
      // Get check-in and check-out times from guard's shift details and convert to UTC
      const checkinTimeUTC = this.convertToUTC(guardData?.shiftDetails?.checkinTime);
      const checkoutTimeUTC = this.convertToUTC(guardData?.shiftDetails?.checkoutTime);

      await firstValueFrom(
        this.commonService.approveOvertime(
          this.finishAssignmentData.id,
          guardData.id,
          checkinTimeUTC,
          checkoutTimeUTC
        )
      );

      // Reload shift details from API to get the updated data
      await this.loadShiftDetails();

      // Update assignment status from "Requesting Approval" (6) to "Finished" (4)
      this.finishAssignmentData.status = 4;
      this.isRequestApproal = false;

      this.hasDataChanged = true;
      this.snackbarService.showSuccess(this.translate.instant('guard-shift-details.finish.overtime-approved'));
    } catch (error) {
      console.error('Error approving overtime:', error);
      this.snackbarService.showError(this.translate.instant('guard-shift-details.finish.overtime-approve-error'));
    } finally {
      this.loadingService.hideLoader();
    }
  }

  private async rejectOvertime(guardData: any): Promise<void> {
    this.loadingService.showLoader();
    try {
      // Get check-in and check-out times from guard's shift details and convert to UTC
      const checkinTimeUTC = this.convertToUTC(guardData?.shiftDetails?.checkinTime);
      const checkoutTimeUTC = this.convertToUTC(guardData?.shiftDetails?.checkoutTime);

      await firstValueFrom(
        this.commonService.rejectOvertime(
          this.finishAssignmentData.id,
          guardData.id,
          checkinTimeUTC,
          checkoutTimeUTC
        )
      );

      // Reload shift details from API to get the updated data
      await this.loadShiftDetails();

      this.hasDataChanged = true;
      this.snackbarService.showSuccess(this.translate.instant('guard-shift-details.finish.overtime-rejected'));
    } catch (error) {
      console.error('Error rejecting overtime:', error);
      this.snackbarService.showError(this.translate.instant('guard-shift-details.finish.overtime-reject-error'));
    } finally {
      this.loadingService.hideLoader();
    }
  }
}
